import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ImagePlus, Loader2, X } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, formatErr, uploadFile } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSite } from "../../context/SiteContext";

export const PLATFORMS = ["PlayStation 5", "PlayStation 4", "Xbox Series X|S", "Xbox One", "PC", "Nintendo Switch"];
const CORE_KEYS = ["name", "email", "platform", "game_version", "service", "service_option", "current_progress", "target_progress", "requirements", "timeframe"];
const inCls = "h-12 bg-[#040a1a] border-[#007bff]/35";

function Pick({ value, onChange, items, placeholder, testid }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={inCls} data-testid={testid}><SelectValue placeholder={placeholder || "Choose"} /></SelectTrigger>
      <SelectContent className="bg-[#060d20] border-[#007bff]/40">
        {items.filter(Boolean).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Shots({ shots, setShots, busy, setBusy }) {
  const onFile = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - shots.length);
    setBusy(true);
    try {
      for (const file of files) {
        const r = await uploadFile(file, { scope: "quote" });
        setShots((s) => [...s, r]);
      }
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap gap-3">
      {shots.map((s, i) => (
        <div key={s.path} className="relative w-20 h-20 rounded-lg border border-[#007bff]/40 overflow-hidden bg-black/40 grid place-items-center text-[10px] text-slate-400 p-1 text-center">
          {s.name}
          <button type="button" onClick={() => setShots(shots.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5"><X className="w-3 h-3" /></button>
        </div>
      ))}
      {shots.length < 5 && (
        <label className="w-20 h-20 rounded-lg border border-dashed border-[#00d9ff]/50 grid place-items-center cursor-pointer text-[#00d9ff] hover:bg-[#007bff]/10" data-testid="quote-upload-label">
          <ImagePlus className="w-6 h-6" />
          <input type="file" accept="image/*" multiple hidden onChange={onFile} data-testid="quote-upload-input" />
        </label>
      )}
    </div>
  );
}

function FieldInput({ f, value, set, ctx }) {
  const tid = `quote-field-${f.key}`;
  const { services, svc } = ctx;
  if (f.type === "service") return <Pick value={value} onChange={ctx.setService} items={services.map((s) => s.title)} placeholder={f.placeholder} testid="quote-service-select" />;
  if (f.type === "service_option") return <Pick value={value} onChange={set} items={svc?.bullets || []} placeholder={f.placeholder} testid="quote-option-select" />;
  if (f.type === "select") {
    const items = f.key === "platform" && svc?.badge?.includes("PS5") ? ["PlayStation 5"] : f.options || [];
    return <Pick value={value} onChange={set} items={items} placeholder={f.placeholder} testid={f.key === "platform" ? "quote-platform-select" : tid} />;
  }
  if (f.type === "checkboxes")
    return (
      <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1" data-testid={tid}>
        {(f.options || []).filter(Boolean).map((o) => {
          const arr = Array.isArray(value) ? value : [];
          return (
            <label key={o} className="flex items-center gap-2 text-sm text-slate-200">
              <Checkbox checked={arr.includes(o)} onCheckedChange={(c) => set(c ? [...arr, o] : arr.filter((x) => x !== o))} />{o}
            </label>
          );
        })}
      </div>
    );
  if (f.type === "textarea") return <Textarea rows={4} required={f.required} value={value || ""} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} className="bg-[#040a1a] border-[#007bff]/35" data-testid={tid} />;
  const ph = f.placeholder || (svc?.icon === "overall" && f.service_label ? (f.service_label === "current" ? "e.g. 78" : "e.g. 99") : "");
  const type = { email: "email", number: "number", date: "date" }[f.type] || "text";
  return <Input type={type} required={f.required} value={value || ""} placeholder={ph} onChange={(e) => set(e.target.value)} className={inCls} data-testid={tid} />;
}

export function QuoteForm({ onDone }) {
  const { site } = useSite();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const services = useMemo(() => site?.services || [], [site]);
  const fields = useMemo(() => (site?.content?.quote_form?.fields || []).filter((f) => !f.hidden), [site]);
  const [vals, setVals] = useState({ game_version: "NBA 2K26" });
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setVals((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (user) setVals((p) => ({ ...p, name: p.name || user.name, email: p.email || user.email }));
  }, [user]);
  useEffect(() => {
    const s = services.find((x) => x.slug === params.get("service"));
    if (s) setVals((p) => ({ ...p, service: s.title }));
  }, [services, params]);

  const svc = services.find((s) => s.title === vals.service);
  const ctx = { services, svc, setService: (v) => setVals((p) => ({ ...p, service: v, service_option: "", platform: "" })) };
  const labelOf = (f) => (f.service_label && svc ? svc[`${f.service_label}_label`] || f.label : f.label);
  const visible = fields.filter((f) => f.type !== "service_option" || svc?.bullets?.length);

  const submit = async (e) => {
    e.preventDefault();
    const missing = visible.find((f) => f.required && f.type !== "file" && !(Array.isArray(vals[f.key]) ? vals[f.key].length : String(vals[f.key] || "").trim()));
    if (missing) return toast.error(`${labelOf(missing)} is required`);
    if (visible.some((f) => f.type === "file" && f.required) && !shots.length) return toast.error("Please upload a screenshot");
    setBusy(true);
    try {
      const shownKeys = new Set(visible.map((f) => f.key));
      const core = Object.fromEntries(CORE_KEYS.map((k) => [k, shownKeys.has(k) ? vals[k] || "" : ""]));
      const custom = Object.fromEntries(fields.filter((f) => !f.core).map((f) => [f.key, vals[f.key] ?? ""]));
      const { data } = await api.post("/quotations", { ...core, custom, screenshots: shots });
      setDone(data);
      onDone?.(data);
      toast.success("Quotation request submitted");
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };

  if (done)
    return (
      <div className="card-neon p-8 text-center" data-testid="quote-success">
        <CheckCircle2 className="w-14 h-14 mx-auto text-[#00d9ff]" />
        <h3 className="font-display text-4xl text-metal mt-3">Request {done.number} received</h3>
        <p className="text-slate-300 mt-2">Our team will review your request and send your quotation{user ? " to your dashboard" : ` to ${done.email}`}.</p>
        {!user && <p className="text-slate-400 mt-2 text-sm">Create an account with the same email to track and accept your quotation online.</p>}
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to={user ? "/dashboard/quotations" : "/register"} className="btn-electric" data-testid="quote-success-cta">{user ? "View my quotations" : "Create account"}</Link>
          <button className="btn-ghost" onClick={() => { setDone(null); setVals({ name: vals.name, email: vals.email, game_version: "NBA 2K26" }); setShots([]); }} data-testid="quote-new-btn">New request</button>
        </div>
      </div>
    );

  return (
    <form onSubmit={submit} className="card-neon p-5 sm:p-8 grid gap-5 sm:grid-cols-2" data-testid="quote-form">
      {visible.map((f) => (
        <div key={f.key} className={`space-y-2 ${f.width === "full" || f.type === "file" ? "sm:col-span-2" : ""}`} data-testid={`quote-field-wrap-${f.key}`}>
          <Label className="font-cond uppercase tracking-wider text-slate-300">{labelOf(f)}{f.required && <span className="text-[#00d9ff]"> *</span>}</Label>
          {f.type === "file" ? <Shots shots={shots} setShots={setShots} busy={busy} setBusy={setBusy} /> : <FieldInput f={f} value={vals[f.key]} set={set(f.key)} ctx={ctx} />}
          {f.help && <p className="text-xs text-slate-500">{f.help}</p>}
        </div>
      ))}
      <div className="sm:col-span-2">
        <button disabled={busy} className="btn-electric w-full h-14 text-lg" data-testid="quote-submit-btn">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : site?.content?.quote_section?.button_text || "Submit Quotation Request"}
        </button>
      </div>
    </form>
  );
}
